// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, ClipboardList, Loader2, Plus, Pencil, Trash2, Pin, Package, LayoutTemplate,
} from "lucide-react";

const FN = "rle-manage-event-ops";

async function callOps(body: any) {
  const { data, error } = await invokeEdgeFunction(FN, { body });
  if (error) throw new Error(error.message || "Request failed");
  if (data && data.ok === false) throw new Error(data.error || "Request failed");
  return data;
}

const num = (n: any) => (n == null || n === "" ? 0 : Number(n));
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : null);

const PRIORITIES = ["low", "normal", "high", "urgent"];
const PRIORITY_VARIANT: Record<string, any> = {
  urgent: "destructive", high: "default", normal: "secondary", low: "outline",
};
const INV_STATUS = ["needed", "ordered", "allocated", "packed", "returned"];

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </CardContent>
  </Card>
);

/* ---------------- Task dialog ---------------- */
const emptyTask = { title: "", category: "", priority: "normal", due_date: "", assigned_to: "", notes: "" };

const TaskDialog = ({ open, onOpenChange, initial, onSave, busy }: any) => {
  const [form, setForm] = useState<any>(emptyTask);
  React.useEffect(() => { if (open) setForm({ ...emptyTask, ...(initial || {}) }); }, [open, initial]);
  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit task" : "Add task"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Title</Label><Input value={form.title} onChange={set("title")} /></div>
          <div><Label>Category</Label><Input value={form.category ?? ""} onChange={set("category")} placeholder="Setup, Staffing…" /></div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority || "normal"} onValueChange={(v) => setForm((f: any) => ({ ...f, priority: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date || ""} onChange={set("due_date")} /></div>
          <div><Label>Assigned to</Label><Input value={form.assigned_to ?? ""} onChange={set("assigned_to")} /></div>
          <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes ?? ""} onChange={set("notes")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={busy || !form.title}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Catalog item dialog ---------------- */
const emptyItem = { name: "", category: "", unit: "", default_quantity: "", notes: "" };

const ItemDialog = ({ open, onOpenChange, initial, onSave, busy }: any) => {
  const [form, setForm] = useState<any>(emptyItem);
  React.useEffect(() => { if (open) setForm({ ...emptyItem, ...(initial || {}) }); }, [open, initial]);
  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit item" : "Add catalog item"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} /></div>
          <div><Label>Category</Label><Input value={form.category ?? ""} onChange={set("category")} /></div>
          <div><Label>Unit</Label><Input value={form.unit ?? ""} onChange={set("unit")} placeholder="each, box…" /></div>
          <div><Label>Default qty</Label><Input type="number" value={form.default_quantity ?? ""} onChange={set("default_quantity")} /></div>
          <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes ?? ""} onChange={set("notes")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={busy || !form.name}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Event inventory line dialog ---------------- */
const emptyLine = {
  inventory_item_id: "", item_name: "", quantity_needed: "", quantity_allocated: "",
  quantity_used: "", status: "needed", notes: "",
};

const LineDialog = ({ open, onOpenChange, initial, catalog, onSave, busy }: any) => {
  const [form, setForm] = useState<any>(emptyLine);
  React.useEffect(() => { if (open) setForm({ ...emptyLine, ...(initial || {}) }); }, [open, initial]);
  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit line" : "Add inventory line"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Catalog item</Label>
            <Select
              value={form.inventory_item_id || "__none"}
              onValueChange={(v) => setForm((f: any) => ({ ...f, inventory_item_id: v === "__none" ? "" : v }))}
            >
              <SelectTrigger><SelectValue placeholder="Free-text item" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Free-text item</SelectItem>
                {(catalog || []).map((i: any) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!form.inventory_item_id && (
            <div className="sm:col-span-2"><Label>Item name</Label><Input value={form.item_name ?? ""} onChange={set("item_name")} /></div>
          )}
          <div><Label>Needed</Label><Input type="number" value={form.quantity_needed ?? ""} onChange={set("quantity_needed")} /></div>
          <div><Label>Allocated</Label><Input type="number" value={form.quantity_allocated ?? ""} onChange={set("quantity_allocated")} /></div>
          <div><Label>Used</Label><Input type="number" value={form.quantity_used ?? ""} onChange={set("quantity_used")} /></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status || "needed"} onValueChange={(v) => setForm((f: any) => ({ ...f, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{INV_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes ?? ""} onChange={set("notes")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onSave(form)}
            disabled={busy || (!form.inventory_item_id && !form.item_name)}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Page ---------------- */
export default function OperatorEventOpsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const orgId = profile?.organization_id ?? null;
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const [taskDialog, setTaskDialog] = useState<{ open: boolean; initial: any }>({ open: false, initial: null });
  const [itemDialog, setItemDialog] = useState<{ open: boolean; initial: any }>({ open: false, initial: null });
  const [lineDialog, setLineDialog] = useState<{ open: boolean; initial: any }>({ open: false, initial: null });
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateId, setTemplateId] = useState<string>("");
  const [noteDraft, setNoteDraft] = useState("");
  const [editingNote, setEditingNote] = useState<any>(null);

  const summaryQuery = useQuery({
    queryKey: ["ops-summary", eventId],
    enabled: !!eventId,
    queryFn: async () => (await callOps({ action: "summary", event_id: eventId }))?.summary ?? {},
  });
  const checklistQuery = useQuery({
    queryKey: ["ops-checklist", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const d = await callOps({ action: "checklist_list", event_id: eventId });
      return d?.tasks ?? d?.checklist ?? [];
    },
  });
  const templatesQuery = useQuery({
    queryKey: ["ops-templates"],
    enabled: templateOpen,
    queryFn: async () => (await callOps({ action: "templates_list" }))?.templates ?? [],
  });
  const catalogQuery = useQuery({
    queryKey: ["ops-catalog", orgId],
    enabled: !!orgId,
    queryFn: async () => (await callOps({ action: "inventory_items_list", organization_id: orgId }))?.items ?? [],
  });
  const eventInvQuery = useQuery({
    queryKey: ["ops-event-inventory", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const d = await callOps({ action: "event_inventory_list", event_id: eventId });
      return d?.lines ?? d?.inventory ?? [];
    },
  });
  const notesQuery = useQuery({
    queryKey: ["ops-notes", eventId],
    enabled: !!eventId,
    queryFn: async () => (await callOps({ action: "notes_list", event_id: eventId }))?.notes ?? [],
  });

  const summary = summaryQuery.data ?? {};
  const tasks = checklistQuery.data ?? [];
  const catalog = catalogQuery.data ?? [];
  const lines = eventInvQuery.data ?? [];
  const notes = useMemo(
    () => [...(notesQuery.data ?? [])].sort((a: any, b: any) => (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0)),
    [notesQuery.data],
  );

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    tasks.forEach((t: any) => {
      const k = t.category || "General";
      (g[k] ||= []).push(t);
    });
    return g;
  }, [tasks]);

  const refresh = (keys: string[]) =>
    keys.forEach((k) => qc.invalidateQueries({ queryKey: [k, k === "ops-catalog" ? orgId : eventId] }));

  const run = async (body: any, msg: string, keys: string[], close?: () => void) => {
    setBusy(true);
    try {
      await callOps(body);
      toast.success(msg);
      close?.();
      qc.invalidateQueries({ queryKey: ["ops-summary", eventId] });
      refresh(keys);
    } catch (e: any) {
      toast.error(e.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div>
        <button onClick={() => navigate(-1)} className="mb-2 flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </button>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <ClipboardList className="h-6 w-6" /> Event Management
        </h1>
        <p className="text-muted-foreground">Run-of-show checklist, inventory, and operational notes.</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="checklist">Checklist</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          {summaryQuery.isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Tile
                label="Checklist"
                value={`${num(summary.checklist_done)} / ${num(summary.checklist_total)}`}
              />
              <Tile label="Urgent open" value={String(num(summary.urgent_open))} />
              <Tile
                label="Inventory needed vs allocated"
                value={`${num(summary.inventory_needed)} / ${num(summary.inventory_allocated)}`}
              />
              <Tile label="Notes" value={String(num(summary.notes ?? summary.notes_count))} />
            </div>
          )}
        </TabsContent>

        {/* Checklist */}
        <TabsContent value="checklist" className="mt-4 space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTemplateOpen(true)}>
              <LayoutTemplate className="mr-2 h-4 w-4" /> Apply template
            </Button>
            <Button onClick={() => setTaskDialog({ open: true, initial: null })}>
              <Plus className="mr-2 h-4 w-4" /> Add task
            </Button>
          </div>

          {checklistQuery.isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : tasks.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">No tasks yet — add one or apply a template.</CardContent></Card>
          ) : (
            Object.entries(grouped).map(([cat, items]) => (
              <Card key={cat}>
                <CardHeader className="pb-2"><CardTitle className="text-base">{cat}</CardTitle></CardHeader>
                <CardContent className="space-y-1 pb-4">
                  {items.map((t: any) => (
                    <div key={t.id} className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50">
                      <Checkbox
                        checked={!!t.done}
                        onCheckedChange={(v) =>
                          run({ action: "checklist_toggle", id: t.id, done: !!v }, "Task updated", ["ops-checklist"])
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <p className={`truncate text-sm ${t.done ? "text-muted-foreground line-through" : ""}`}>{t.title}</p>
                        {t.assigned_to && <p className="text-xs text-muted-foreground">{t.assigned_to}</p>}
                      </div>
                      {t.priority && (
                        <Badge variant={PRIORITY_VARIANT[t.priority] || "secondary"}>{t.priority}</Badge>
                      )}
                      {fmtDate(t.due_date) && <Badge variant="outline">Due {fmtDate(t.due_date)}</Badge>}
                      <Button variant="ghost" size="sm" onClick={() => setTaskDialog({ open: true, initial: t })}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Inventory */}
        <TabsContent value="inventory" className="mt-4 space-y-6">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">This event</CardTitle>
              <Button size="sm" onClick={() => setLineDialog({ open: true, initial: null })}>
                <Plus className="mr-2 h-4 w-4" /> Add line
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Needed</TableHead>
                    <TableHead>Allocated</TableHead>
                    <TableHead>Used</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Nothing tracked yet</TableCell></TableRow>
                  )}
                  {lines.map((l: any) => {
                    const shortfall = num(l.quantity_needed) - num(l.quantity_allocated);
                    return (
                      <TableRow key={l.id}>
                        <TableCell className="font-medium">
                          {l.item_name || l.name || "—"}
                          {shortfall > 0 && (
                            <Badge variant="destructive" className="ml-2">Short {shortfall}</Badge>
                          )}
                        </TableCell>
                        <TableCell>{num(l.quantity_needed)}</TableCell>
                        <TableCell>{num(l.quantity_allocated)}</TableCell>
                        <TableCell>{num(l.quantity_used)}</TableCell>
                        <TableCell><Badge variant="secondary">{l.status || "needed"}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setLineDialog({ open: true, initial: l })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4" /> Catalog (organization)
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => setItemDialog({ open: true, initial: null })}>
                <Plus className="mr-2 h-4 w-4" /> Add item
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Default qty</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catalog.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No catalog items</TableCell></TableRow>
                  )}
                  {catalog.map((i: any) => (
                    <TableRow key={i.id}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell>{i.category || "—"}</TableCell>
                      <TableCell>{i.unit || "—"}</TableCell>
                      <TableCell>{i.default_quantity ?? "—"}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setItemDialog({ open: true, initial: i })}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes */}
        <TabsContent value="notes" className="mt-4 space-y-4">
          <Card>
            <CardContent className="space-y-3 p-4">
              <Label>{editingNote ? "Edit note" : "New note"}</Label>
              <Textarea
                rows={3}
                value={editingNote ? editingNote.content : noteDraft}
                onChange={(e) =>
                  editingNote
                    ? setEditingNote({ ...editingNote, content: e.target.value })
                    : setNoteDraft(e.target.value)
                }
                placeholder="Anything the ops team should know…"
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!!editingNote?.is_pinned}
                    onCheckedChange={(v) =>
                      setEditingNote((n: any) => ({ ...(n || { content: noteDraft }), is_pinned: !!v }))
                    }
                  />
                  Pin this note
                </label>
                <div className="flex gap-2">
                  {editingNote && (
                    <Button variant="outline" onClick={() => setEditingNote(null)}>Cancel</Button>
                  )}
                  <Button
                    disabled={busy || !(editingNote ? editingNote.content : noteDraft).trim()}
                    onClick={() =>
                      run(
                        {
                          action: "note_save",
                          event_id: eventId,
                          id: editingNote?.id,
                          content: editingNote ? editingNote.content : noteDraft,
                          is_pinned: !!editingNote?.is_pinned,
                        },
                        "Note saved",
                        ["ops-notes"],
                        () => { setEditingNote(null); setNoteDraft(""); },
                      )
                    }
                  >
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save note
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {notes.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">No notes yet</CardContent></Card>
          ) : (
            notes.map((n: any) => (
              <Card key={n.id}>
                <CardContent className="flex items-start gap-3 p-4">
                  {n.is_pinned && <Pin className="mt-1 h-4 w-4 shrink-0 text-primary" />}
                  <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm">{n.content}</p>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditingNote(n)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        window.confirm("Delete this note?") &&
                        run({ action: "note_delete", id: n.id }, "Note deleted", ["ops-notes"])
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <TaskDialog
        open={taskDialog.open}
        initial={taskDialog.initial}
        busy={busy}
        onOpenChange={(o: boolean) => setTaskDialog((s) => ({ ...s, open: o }))}
        onSave={(form: any) =>
          run(
            { action: "checklist_save", event_id: eventId, id: taskDialog.initial?.id, task: form },
            "Task saved",
            ["ops-checklist"],
            () => setTaskDialog({ open: false, initial: null }),
          )
        }
      />

      <ItemDialog
        open={itemDialog.open}
        initial={itemDialog.initial}
        busy={busy}
        onOpenChange={(o: boolean) => setItemDialog((s) => ({ ...s, open: o }))}
        onSave={(form: any) =>
          run(
            { action: "inventory_item_save", organization_id: orgId, id: itemDialog.initial?.id, item: form },
            "Item saved",
            ["ops-catalog"],
            () => setItemDialog({ open: false, initial: null }),
          )
        }
      />

      <LineDialog
        open={lineDialog.open}
        initial={lineDialog.initial}
        catalog={catalog}
        busy={busy}
        onOpenChange={(o: boolean) => setLineDialog((s) => ({ ...s, open: o }))}
        onSave={(form: any) =>
          run(
            { action: "event_inventory_save", event_id: eventId, id: lineDialog.initial?.id, line: form },
            "Inventory line saved",
            ["ops-event-inventory"],
            () => setLineDialog({ open: false, initial: null }),
          )
        }
      />

      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Apply checklist template</DialogTitle></DialogHeader>
          {templatesQuery.isLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger><SelectValue placeholder="Select a template" /></SelectTrigger>
              <SelectContent>
                {(templatesQuery.data ?? []).map((t: any) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateOpen(false)}>Cancel</Button>
            <Button
              disabled={busy || !templateId}
              onClick={() =>
                run(
                  { action: "apply_template", event_id: eventId, template_id: templateId },
                  "Template applied",
                  ["ops-checklist"],
                  () => { setTemplateOpen(false); setTemplateId(""); },
                )
              }
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
